/* ══════════════════════════════════════════════════════════════════
   BUSTAH — GSAP MOTION LAYER

   Everything the theme already does — the film scrubber, the deck, the
   scroll-linked foreground, the panels — is left exactly as it was.
   This file sits on top and adds the arrival: type that rises on a mask,
   cards that deal in, a product photograph that flies into the bag when
   it is added, and a panel whose contents assemble as they land.

   Three switches turn all of it off, in this order:
     · prefers-reduced-motion (or ?motion=off) — window.__motion is false
     · the GSAP bundle not loading — nothing here runs
     · settings → Motion → GSAP layer, in the theme editor
   In every one of those cases the theme behaves precisely as it did
   before this file existed: no class is added, no style is injected and
   no element is ever left hidden by something that never ran.
   ══════════════════════════════════════════════════════════════════ */
(function () {
  var root = document.documentElement;
  if (!window.__motion) return;                 /* the visitor asked for stillness */
  if (!window.gsap) return;                     /* bundle blocked — theme unchanged */

  var g = window.gsap, ST = window.ScrollTrigger;
  if (ST && g.registerPlugin) g.registerPlugin(ST);
  g.defaults({ ease: 'power3.out' });
  root.classList.add('gsap-on');

  var FINE = window.matchMedia('(pointer:fine)').matches;
  function deckOn() { return !!(window.__deckOn && window.__deckOn()); }
  function vh() { return window.innerHeight; }

  /* ── the stylesheet this layer needs ──────────────────────────────
     Injected from here rather than shipped as its own asset so it can
     never arrive without the script that depends on it. The deck rule
     is the important one: bustah.css holds .rv blocks open with
     !important inside the deck, which no inline style can beat, so
     while this layer is driving them that becomes an ordinary
     declaration — scoped to .gsap-on, which only exists once this file
     has run. */
  var css = document.createElement('style');
  css.id = 'bustah-gsap-css';
  css.textContent =
    /* a heading is cut into words, each in its own clipped box, so the
       line can rise on a mask. The padding/negative-margin pair widens
       the clip for descenders and italic overhang without moving anything */
    '.wl{display:inline-block;overflow:hidden;vertical-align:top;' +
      'padding:.03em .1em .18em;margin:-.03em -.1em -.18em}' +
    '.wl>i{display:inline-block;font-style:inherit;font-weight:inherit}' +
    /* the deck's hold, restated so inline styles can win */
    'html.gsap-on.deck #page .rv,html.gsap-on.deck #page .rvl,' +
    'html.gsap-on.deck #page .rvr,html.gsap-on.deck #page .stg>*' +
      '{opacity:1;transform:none}' +
    /* GSAP owns the card's transform; CSS must not ease it a second time */
    '.gsap-on .pcard{transition:none}' +
    '.gsap-on .pgrid{perspective:1200px}' +
    /* the photograph that flies into the bag */
    '.fly{position:fixed;z-index:1650;pointer-events:none;border-radius:10px;' +
      'overflow:hidden;box-shadow:0 18px 44px -16px rgba(0,0,0,.85);' +
      'will-change:transform,opacity}' +
    '.fly img{width:100%;height:100%;object-fit:cover;display:block}';
  document.head.appendChild(css);

  /* ══════════ WORDS ══════════
     Structure (<em>, <br>) is preserved; only text nodes are cut up.
     Gradient type — anything painted with background-clip:text — cannot
     be split, because the fill is painted by the element itself, so it
     travels as one piece inside its own mask. */
  function split(el) {
    if (!el) return [];
    if (el.dataset.split) return [].slice.call(el.querySelectorAll('.wl > i'));
    el.dataset.split = '1';
    var words = [];
    (function walk(node) {
      [].slice.call(node.childNodes).forEach(function (n) {
        if (n.nodeType === 3) {
          if (!n.nodeValue.trim()) return;
          var frag = document.createDocumentFragment();
          n.nodeValue.split(/(\s+)/).forEach(function (p) {
            if (!p) return;
            if (/^\s+$/.test(p)) { frag.appendChild(document.createTextNode(' ')); return; }
            var w = document.createElement('span'), i = document.createElement('i');
            w.className = 'wl'; i.textContent = p;
            w.appendChild(i); frag.appendChild(w); words.push(i);
          });
          node.replaceChild(frag, n);
        } else if (n.nodeType === 1 && n.tagName !== 'BR' && !n.classList.contains('wl')) {
          var cs = window.getComputedStyle(n);
          if ((cs.webkitBackgroundClip || cs.backgroundClip) === 'text') {
            var box = document.createElement('span');
            box.className = 'wl';
            if (cs.display === 'block') box.style.display = 'block';
            n.parentNode.insertBefore(box, n);
            box.appendChild(n);
            words.push(n);
            return;
          }
          walk(n);
        }
      });
    })(el);
    return words;
  }

  function pick(scope, sel) { return [].slice.call(scope.querySelectorAll(sel)); }
  function cap(n, each, most) { return { each: each, amount: Math.min(most, n * each) }; }

  /* ══════════ THE ARRIVAL ══════════
     One shape for every section on the site: kicker, heading on a mask,
     the rule drawing itself, the copy, then whatever that section offers
     as its way in — buttons, category chips, cards. */
  function arrival(scope, tl, at) {
    at = at || 0;
    var badge = scope.querySelector('.hbadge'),
        tag   = scope.querySelector('.tag, .p-eyebrow'),
        head  = scope.querySelector('h1, h2.t, .p-title, .page-h, .rel-h'),
        rule  = scope.querySelector('.rule'),
        copy  = pick(scope, '.hsub, .lede, .hnote, .gridcap, .page-lede, .crumbs, .pdsub'),
        acts  = pick(scope, '.hacts .btn, .center > .btn, .shopmore, .tabrow .tab, ' +
                            '.socials a, .chip, .scroll-cue'),
        words = split(head);

    if (badge) tl.fromTo(badge, { opacity: 0, scale: .82, y: -10 },
      { opacity: 1, scale: 1, y: 0, duration: .6, ease: 'back.out(2.2)', clearProps: 'transform' }, at);
    if (tag) tl.fromTo(tag, { opacity: 0, x: -28 },
      { opacity: 1, x: 0, duration: .65, clearProps: 'transform' }, at + .05);
    if (words.length) tl.fromTo(words, { yPercent: 120 },
      { yPercent: 0, duration: .95, ease: 'power4.out', stagger: cap(words.length, .055, .7) }, at + .1);
    if (rule) tl.fromTo(rule, { scaleX: 0, transformOrigin: 'left center' },
      { scaleX: 1, duration: .7, ease: 'power2.out' }, at + .42);
    if (copy.length) tl.fromTo(copy, { opacity: 0, y: 22 },
      { opacity: 1, y: 0, duration: .7, stagger: .08, clearProps: 'transform' }, at + .38);
    if (acts.length) tl.fromTo(acts, { opacity: 0, y: 26, scale: .94 },
      { opacity: 1, y: 0, scale: 1, duration: .6, ease: 'back.out(1.7)',
        stagger: cap(acts.length, .045, .6), clearProps: 'transform' }, at + .5);
    return tl;
  }

  /* the cards a section deals in — inside the deck the stylesheet has
     handed the staggered rows to this layer; outside it the theme's own
     scroll-linked reveal still owns them and must not be fought over */
  function cardsOf(sec) {
    return deckOn() ? pick(sec, '.stg > *')
                    : pick(sec, '.pgrid:not(.stg) .pcard, .rel .pcard');
  }
  function dealIn(tl, cards, at) {
    if (!cards.length) return tl;
    tl.fromTo(cards, { opacity: 0, y: 44, rotateX: 8, scale: .95 },
      { opacity: 1, y: 0, rotateX: 0, scale: 1, duration: .8,
        stagger: cap(cards.length, .06, .7), clearProps: 'transform' }, at);
    return tl;
  }

  /* ══════════ HOMEPAGE SECTIONS ══════════
     These carry the theme's own .rv blocks, which the stylesheet has
     already hidden — so the timeline is built up front and the content
     is never briefly visible before it arrives.

     Inside the deck a section reveals itself the moment it starts fading
     in, so the two movements read as one arrival. Outside it — a short
     screen, or the first section, which scrolls rather than being pinned
     — ScrollTrigger drives the same timeline. */
  var drivers = [];

  function drive(sec) {
    if (sec.dataset.gsapOn) return;
    sec.dataset.gsapOn = '1';
    var tl = null, armed = true;
    function build() {
      if (!tl) { tl = g.timeline({ paused: true }); arrival(sec, tl, 0); dealIn(tl, cardsOf(sec), .34); }
      return tl;
    }
    function play() { if (!armed) return; armed = false; build().play(0); }
    function reset() { if (armed) return; armed = true; build().pause(0); }
    function flowing() { return !deckOn() || sec.classList.contains('deck-flow'); }
    function sync() {
      if (flowing()) return;
      var o = parseFloat(sec.style.opacity || '0');
      if (sec.style.visibility === 'hidden' || o <= .02) reset();
      else if (o > .06) play();
    }
    build();                                   /* from-states, before first paint */
    new MutationObserver(sync).observe(sec, { attributes: true, attributeFilter: ['class', 'style'] });
    if (ST) ST.create({
      trigger: sec, start: 'top 88%', end: 'bottom 8%',
      onEnter:     function () { if (flowing()) play(); },
      onEnterBack: function () { if (flowing()) play(); },
      onLeaveBack: function () { if (flowing()) reset(); }
    });
    drivers.push({ sec: sec, play: play, armed: function () { return armed; } });
    sync();
    onScreen(sec) && flowing() && play();
  }

  function onScreen(el) {
    var r = el.getBoundingClientRect();
    return r.top < vh() * .95 && r.bottom > 0;
  }

  /* Nothing on this page may stay hidden because a trigger never fired.
     Anything still armed and on the screen is played outright. */
  function failsafe() {
    drivers.forEach(function (d) { if (d.armed() && onScreen(d.sec)) d.play(); });
  }

  pick(document, '#page section').forEach(function (sec) {
    if (sec.querySelector('.rv, .rvl, .rvr, .stg')) drive(sec);
  });

  /* ══════════ INNER TEMPLATES ══════════
     Collection, product, search, blog, cart and pages carry no .rv, so
     their content is visible with JavaScript off — which means it must
     stay visible here too. These animate FROM a hidden state at the
     moment they arrive, and are never hidden before that. */
  if (ST) {
    pick(document, '#page .page-sec').forEach(function (sec) {
      var head = pick(sec, ':scope > .wrap > .page-h, :scope > .wrap > .crumbs, :scope > .wrap > .page-lede');
      if (head.length) ST.create({
        trigger: sec, start: 'top 90%', once: true,
        onEnter: function () {
          var tl = g.timeline();
          arrival(sec, tl, 0);
        }
      });
      var pd = sec.querySelector('.pd');
      if (pd) ST.create({
        trigger: pd, start: 'top 88%', once: true,
        onEnter: function () { revealDetail(pd); }
      });
    });

    /* every product grid outside the homepage's staggered rows */
    ST.batch(pick(document, '#page .pgrid:not(.stg) .pcard'), {
      start: 'top 92%',
      onEnter: function (batch) {
        g.from(batch, { opacity: 0, y: 40, rotateX: 8, scale: .96, duration: .75,
          stagger: cap(batch.length, .06, .5), overwrite: true, clearProps: 'transform' });
      }
    });
  }

  /* ══════════ A GRID THAT CHANGED ══════════
     Switching category, or Show more, replaces or extends the grid. The
     theme calls __rescan so its own reveal engine picks the new cards
     up; this deals them in on top of that. */
  var rescan = window.__rescan;
  window.__rescan = function () {
    if (typeof rescan === 'function') rescan.apply(null, arguments);
    requestAnimationFrame(function () {
      pick(document, '#page .pgrid').forEach(function (grid) {
        if (!onScreen(grid)) return;
        var fresh = pick(grid, '.pcard').filter(function (c) { return !c.dataset.dealt; });
        if (!fresh.length) return;
        fresh.forEach(function (c) { c.dataset.dealt = '1'; });
        g.fromTo(fresh, { opacity: 0, y: 30, scale: .96 },
          { opacity: 1, y: 0, scale: 1, duration: .6, overwrite: true,
            stagger: cap(fresh.length, .05, .5), clearProps: 'transform' });
      });
      if (ST) ST.refresh();
    });
  };

  /* ══════════ PANELS ══════════
     A panel is a shell at first paint and its contents are fetched, so
     the reveal is driven by the arrival of the content, not by the
     opening of the panel. */
  function revealDetail(pd) {
    var tl = g.timeline();
    var shot   = pd.querySelector('.pdshot'),
        thumbs = pick(pd, '.pdthumbs > *'),
        price  = pd.querySelector('.pdprice'),
        opts   = pick(pd, '.pdopt'),
        acts   = pick(pd, '.pdacts > *'),
        rest   = pick(pd, '.pddesc, .pdnote, .pdperma');
    if (shot) tl.fromTo(shot, { opacity: 0, scale: .94 },
      { opacity: 1, scale: 1, duration: .8, clearProps: 'transform' }, 0);
    if (thumbs.length) tl.fromTo(thumbs, { opacity: 0, y: 16 },
      { opacity: 1, y: 0, duration: .5, stagger: cap(thumbs.length, .05, .4), clearProps: 'transform' }, .2);
    if (price) tl.fromTo(price, { opacity: 0, y: 18 },
      { opacity: 1, y: 0, duration: .6, clearProps: 'transform' }, .16);
    if (opts.length) tl.fromTo(opts, { opacity: 0, y: 18 },
      { opacity: 1, y: 0, duration: .55, stagger: .07, clearProps: 'transform' }, .24);
    if (acts.length) tl.fromTo(acts, { opacity: 0, y: 22, scale: .96 },
      { opacity: 1, y: 0, scale: 1, duration: .55, ease: 'back.out(1.7)',
        stagger: .07, clearProps: 'transform' }, .34);
    if (rest.length) tl.fromTo(rest, { opacity: 0, y: 16 },
      { opacity: 1, y: 0, duration: .5, stagger: .06, clearProps: 'transform' }, .42);
    return tl;
  }

  function revealPanel(p) {
    var body = p.querySelector('.p-body');
    if (!body) return;
    var head = pick(p, '.p-head .p-eyebrow, .p-head .p-title'),
        pd    = body.querySelector('.pd'),
        rows  = pick(body, '.bagrow'),
        cards = pick(body, '.pcard'),
        tail  = pick(body, '.bagfoot, .bagend, .bagdisc, .bagtot, .p-wait, .bag-empty');

    g.killTweensOf([head, rows, cards, tail]);
    var tl = g.timeline();
    if (head.length) tl.fromTo(head, { opacity: 0, y: 12 },
      { opacity: 1, y: 0, duration: .45, stagger: .06, clearProps: 'transform' }, 0);

    if (pd) { tl.add(revealDetail(pd), .1); return; }

    if (rows.length) tl.fromTo(rows, { opacity: 0, x: 46 },
      { opacity: 1, x: 0, duration: .6, stagger: cap(rows.length, .07, .5), clearProps: 'transform' }, .1);
    if (cards.length) tl.fromTo(cards, { opacity: 0, y: 34, scale: .96 },
      { opacity: 1, y: 0, scale: 1, duration: .65,
        stagger: cap(cards.length, .05, .6), clearProps: 'transform' }, .12);
    if (tail.length) tl.fromTo(tail, { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: .55, stagger: .07, clearProps: 'transform' }, .26);
    if (!rows.length && !cards.length && !tail.length) {
      var any = pick(body, ':scope > *');
      if (any.length) tl.fromTo(any, { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: .55, stagger: .06, clearProps: 'transform' }, .1);
    }
  }

  function watchPanel(p) {
    if (p.dataset.gsapOn) return;
    p.dataset.gsapOn = '1';
    var body = p.querySelector('.p-body'), pending = false;
    function queue() {
      if (pending || !p.classList.contains('open')) return;
      pending = true;
      requestAnimationFrame(function () { pending = false; revealPanel(p); });
    }
    /* the panel opening, and the fetched contents landing in it */
    var was = p.classList.contains('open');
    new MutationObserver(function () {
      var now = p.classList.contains('open');
      if (now === was) return;
      was = now;
      if (now) queue();
    }).observe(p, { attributes: true, attributeFilter: ['class'] });
    if (body) new MutationObserver(queue).observe(body, { childList: true });
  }
  pick(document, '.panel').forEach(watchPanel);
  /* panels are hoisted to <body> by the theme, and the editor can bring
     new ones in at any time */
  new MutationObserver(function (recs) {
    recs.forEach(function (r) {
      [].slice.call(r.addedNodes).forEach(function (n) {
        if (n.nodeType !== 1) return;
        if (n.classList && n.classList.contains('panel')) watchPanel(n);
        if (n.querySelectorAll) pick(n, '.panel').forEach(watchPanel);
      });
    });
  }).observe(document.body, { childList: true });

  /* ══════════ INTO THE BAG ══════════
     The photograph of the thing being bought leaves the card, arcs across
     the screen and drops into the bag. It is the one moment in a shop
     where an animation is doing a job: it says WHICH item went in, and
     WHERE the bag is, without a word of copy. */
  function bagFab() { return document.querySelector('.fab-bag'); }

  function fly(img) {
    var fab = bagFab();
    if (!img || !fab || !img.complete) return;
    var a = img.getBoundingClientRect(), b = fab.getBoundingClientRect();
    if (!a.width || !b.width) return;
    /* only from a photograph the customer can actually see leaving */
    if (a.bottom < 0 || a.top > window.innerHeight || a.right < 0 || a.left > window.innerWidth) {
      popBag(); return;
    }

    var node = document.createElement('div');
    node.className = 'fly';
    node.style.cssText += ';left:' + a.left + 'px;top:' + a.top + 'px;' +
      'width:' + a.width + 'px;height:' + a.height + 'px';
    var clone = document.createElement('img');
    clone.src = img.currentSrc || img.src;
    clone.alt = '';
    node.appendChild(clone);
    document.body.appendChild(node);

    var dx = (b.left + b.width / 2) - (a.left + a.width / 2),
        dy = (b.top + b.height / 2) - (a.top + a.height / 2);

    /* x and y are tweened separately: together they would draw a straight
       line, and the two eases are what bend it into an arc */
    var tl = g.timeline({ onComplete: function () { node.remove(); popBag(); } });
    tl.to(node, { x: dx, duration: .8, ease: 'power1.inOut' }, 0)
      .to(node, { y: dy, duration: .8, ease: 'power2.in' }, 0)
      .to(node, { scale: .16, rotate: 14, duration: .8, ease: 'power2.in' }, 0)
      .to(node, { opacity: 0, duration: .22 }, .58);
  }

  function popBag() {
    var fab = bagFab();
    if (!fab) return;
    g.fromTo(fab, { scale: 1 },
      { scale: 1.22, duration: .18, ease: 'power2.out', yoyo: true, repeat: 1, clearProps: 'transform' });
  }

  /* capture, so the photograph leaves at the moment of the press rather
     than when the network answers */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-add]');
    if (!btn || btn.disabled) return;
    var host = btn.closest('.pd') || btn.closest('.pcard') || btn.closest('.panel');
    var img = host && host.querySelector('.pdshot img, .pshot img, img');
    fly(img);
  }, true);

  /* the counters on the two floating buttons answer when they change */
  pick(document, '[data-bag-count], [data-save-count]').forEach(function (b) {
    new MutationObserver(function () {
      if (b.hidden) return;
      g.fromTo(b, { scale: 0 },
        { scale: 1, duration: .45, ease: 'back.out(3)', clearProps: 'transform' });
    }).observe(b, { childList: true, characterData: true, subtree: true });
  });

  /* ══════════ THE CARD UNDER THE CURSOR ══════════
     Bound the first time a card is pointed at rather than up front, so a
     grid that arrives later — a category switch, Show more, a fetched
     panel — needs nothing rebound. */
  if (FINE) {
    document.addEventListener('pointerover', function (e) {
      var c = e.target.closest && e.target.closest('.pcard');
      if (!c || c.dataset.tilt) return;
      c.dataset.tilt = '1';
      var rx = g.quickTo(c, 'rotationX', { duration: .5, ease: 'power3' }),
          ry = g.quickTo(c, 'rotationY', { duration: .5, ease: 'power3' }),
          my = g.quickTo(c, 'y',         { duration: .5, ease: 'power3' });
      c.addEventListener('pointermove', function (ev) {
        var r = c.getBoundingClientRect();
        rx(((ev.clientY - r.top) / r.height - .5) * -7);
        ry(((ev.clientX - r.left) / r.width - .5) * 9);
      });
      c.addEventListener('pointerenter', function () { my(-6); });
      c.addEventListener('pointerleave', function () { rx(0); ry(0); my(0); });
      my(-6);
    }, true);
  }

  /* ══════════ THE FURNITURE ══════════
     The header settles in and the floating pair rises, once, on arrival.
     The pair is moved as a group: each button carries its own hover
     transition, and easing the same property twice reads as lag. */
  (function () {
    var nav = document.querySelector('header, .hdr, nav'), fabs = document.querySelector('.fabs');
    if (nav) g.from(nav, { y: -60, opacity: 0, duration: .7, clearProps: 'transform,opacity' });
    if (fabs) g.from(fabs, { y: 70, opacity: 0, duration: .8, delay: .25,
      ease: 'back.out(1.6)', clearProps: 'transform,opacity' });
  })();

  /* ══════════ KEEPING IT HONEST ══════════ */
  window.addEventListener('load', function () {
    if (ST) ST.refresh();
    setTimeout(failsafe, 300);
  });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { if (ST) ST.refresh(); });
  }
  setTimeout(failsafe, 2500);

  /* the theme editor re-renders a section in place */
  document.addEventListener('shopify:section:load', function (e) {
    pick(e.target, 'section').forEach(function (sec) {
      if (sec.querySelector('.rv, .rvl, .rvr, .stg')) drive(sec);
    });
    if (ST) ST.refresh();
    setTimeout(failsafe, 200);
  });
})();
